import type { FC } from "react";
import { useRef, useState, useCallback, type JSX } from "react";
import { Dropdown, DropdownButton } from "@similarweb/ui-components";
import type { ICategory } from "app/common/services/categoryService.types";
import { allTrackers } from "app/services/track/track";
import {
    EllipsisContainer,
    StyledDropdownItem,
    StyledDropdownWrapper,
    StyledIndustriesFilterContainer,
    DropdownAndIconWrapper,
} from "./Styled";
import {
    AutoCompleteContainer,
    AutocompleteLabel,
} from "app/pages/industry-analysis/market-builder/tabs/common/styled";
import { SWReactIcons } from "@similarweb/icons";
import { useMarketBuilderServices } from "app/pages/industry-analysis/market-builder/CustomHooks/useMarketBuilderServices";

interface IIndustryFilterProps {
    selectedIndustry?: ICategory;
    onChangeSelectedIndustry: (category: ICategory) => void;
}

export const Autocomplete: FC<IIndustryFilterProps> = (props) => {
    const { selectedIndustry, onChangeSelectedIndustry } = props;
    const services = useMarketBuilderServices();
    const [selectedId, setSelectedId] = useState<ICategory>(selectedIndustry ?? ({} as ICategory));
    const dropdownRef = useRef(undefined);
    const { categoryService, translate } = services;
    const industries = categoryService
        .getFlattenedCategoriesList()
        ?.filter(({ isCustomCategory }) => !isCustomCategory);
    const dropdownButtonText = translate("industry.research.market.builder.dropdown.button");
    const searchPlaceHolderText = translate("industry.research.market.builder.search.placeholder");

    const onSelect = ({ id }) => {
        const industry = industries.find((industry) => industry.id === id);
        setSelectedId(industry);
        allTrackers.trackEvent("Button", "click", `marketBuilder/chooseIndustry/${industry.categoryId}`);
        dropdownRef.current?.closePopup();
        onChangeSelectedIndustry(industry);
    };

    const selectedIdsMapping = { [selectedId.id]: true };

    const handleSearch = (term: string, item: JSX.Element) => {
        const fullIndustryItem = industries.find((sr) => sr.id === item?.props?.id);
        return fullIndustryItem.text.toLowerCase().includes(term.toLowerCase());
    };

    const renderDropdownButton = useCallback(() => {
        return (
            <DropdownButton height={40}>
                <EllipsisContainer>
                    {Object.keys(selectedId).length > 0 ? selectedId.text : dropdownButtonText}
                </EllipsisContainer>
            </DropdownButton>
        );
    }, [selectedId, dropdownButtonText]);

    const renderDropdownIndustries = () => {
        return industries.map((category, index) => {
            const { id, isChild, text } = category;
            return (
                <StyledDropdownWrapper
                    id={id}
                    key={`id-${index}`}
                    selected={Object.keys(selectedIdsMapping).includes(id)}
                >
                    <DropdownAndIconWrapper>
                        {!isChild && (
                            <SWReactIcons
                                size="sm"
                                iconName="market"
                            />
                        )}
                        <StyledDropdownItem isChild={isChild}>{text}</StyledDropdownItem>
                    </DropdownAndIconWrapper>
                </StyledDropdownWrapper>
            );
        });
    };

    return (
        <AutoCompleteContainer>
            <AutocompleteLabel>
                {translate("industry.research.market.builder.tab.industry.autocomplete.label")}
            </AutocompleteLabel>
            <StyledIndustriesFilterContainer>
                <Dropdown
                    ref={dropdownRef}
                    hasSearch={true}
                    onClick={onSelect}
                    closeOnItemClick={false}
                    appendTo={"body"}
                    selectedIds={selectedIdsMapping}
                    searchFilter={handleSearch}
                    searchPlaceHolder={searchPlaceHolderText}
                    dropdownPopupHeight={360}
                    cssClassContainer={"DropdownContent-container FiltersBarDropdown market-builder-filter"}
                >
                    {[renderDropdownButton(), ...renderDropdownIndustries()]}
                </Dropdown>
            </StyledIndustriesFilterContainer>
        </AutoCompleteContainer>
    );
};
